/**
 * Erros de endpoints com `responseType: 'blob'` chegam com o body JSON do backend
 * DENTRO de um `Blob`: sem o parse, o funil de `getApiErrorMessage` não enxerga
 * `response.data.message` e toda falha de download/exportação vira o fallback
 * genérico, mesmo quando o backend explicou o motivo.
 *
 * `unwrapBlobError` devolve um erro equivalente com o body já parseado, pronto para
 * `Service.handleError`. Se o body não for JSON (HTML de proxy, binário) ou a leitura
 * falhar, devolve o erro ORIGINAL — o status continua decidindo 4xx/5xx e nada é
 * mascarado.
 *
 * Promovido de `StationService.normalizeBlobError` (M3) para uso compartilhado.
 */

interface BlobErrorLike {
  code?: string
  response?: { status?: number; data?: unknown }
}

/**
 * Lê o Blob como texto via `FileReader` em vez de `Blob.text()`: mesmo resultado no
 * browser e disponível no jsdom dos testes, que não implementa `Blob.text`.
 */
function readBlobAsText(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

export async function unwrapBlobError(error: unknown): Promise<unknown> {
  const errorLike = error as BlobErrorLike | null | undefined
  const data = errorLike?.response?.data

  if (!(data instanceof Blob)) return error

  try {
    const parsed: unknown = JSON.parse(await readBlobAsText(data))
    return {
      code: errorLike?.code,
      response: { status: errorLike?.response?.status, data: parsed }
    }
  } catch {
    // Blob não-JSON ou leitura falha: nunca mascarar — segue o erro original.
    return error
  }
}
